import React, { useState, useEffect, useRef } from 'react';
import Icon from './Icon';
import MailboxSwitcher from './MailboxSwitcher';
import { LABELS, LABEL_COLOURS } from '../lib/labels';
import { isV2, classicHref } from '../lib/uiVersion';
import styles from './FolderTree.module.css';

// /V2 (decided once per tab — see lib/uiVersion.js).
const V2 = isV2();

// Map Graph folder displayName → prototype icon name.
const ICONS = {
  inbox:        'inbox',
  drafts:       'draft',
  sentitems:    'sent',
  deleteditems: 'trash',
  junkemail:    'junk',
  archive:      'archive',
  onlinearchive:'archive',
  outbox:       'outbox',
};

const WELL_KNOWN_ORDER = ['inbox', 'drafts', 'sentitems', 'deleteditems', 'junkemail', 'archive', 'outbox'];

// Outlook system folders the user never wants to see in the sidebar.
const HIDDEN_FOLDERS = new Set(['syncissues', 'rssfeeds', 'rsssubscriptions', 'conversationhistory', 'conversationaction settings']);

function isHiddenFolder(f) {
  const key = (f.displayName || '').toLowerCase().replace(/\s/g, '');
  return HIDDEN_FOLDERS.has(key);
}

function iconFor(name) {
  const key = name?.toLowerCase().replace(/\s/g, '');
  return ICONS[key] || 'folder';
}

function sortFolders(folders) {
  const visible = folders.filter(f => !isHiddenFolder(f));
  const wk = [], other = [];
  visible.forEach(f => {
    const key = f.displayName?.toLowerCase().replace(/\s/g, '');
    WELL_KNOWN_ORDER.includes(key) ? wk.push(f) : other.push(f);
  });
  wk.sort((a, b) => {
    const ki = k => WELL_KNOWN_ORDER.indexOf(k.displayName?.toLowerCase().replace(/\s/g, ''));
    return ki(a) - ki(b);
  });
  return [...wk, ...other];
}

// Folders the user is allowed to empty via right-click. Junk + Deleted
// Items are the safe "throwaway" folders; emptying any other folder
// would be a foot-gun (Inbox / Sent / Drafts hold real mail). Keys are
// the lowercase-no-space normalised displayName, same shape used by
// ICONS / WELL_KNOWN_ORDER above.
const EMPTYABLE = new Set(['junkemail', 'deleteditems']);
// Folders whose unread badge renders in muted grey instead of accent
// blue. Junk by definition isn't actionable mail — the count
// shouldn't shout for attention the way an Inbox unread count does.
const MUTED_BADGE = new Set(['junkemail']);

function folderKey(f) {
  return (f.displayName || '').toLowerCase().replace(/\s/g, '');
}

// A sidebar section's open/closed state, remembered per browser once the
// person toggles it — so Labels stays open across reloads if they opened it,
// and across the remount that follows adding a custom label. Until then the
// section starts at `defaultOpen`. Storage can throw (private mode, blocked
// site data); the section then just uses the default.
function useSectionOpen(key, defaultOpen) {
  const storageKey = `sidebar.section.${key}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? defaultOpen : v === '1';
    } catch { return defaultOpen; }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* default next load */ }
  };
  return [open, toggle];
}

// A right-click menu's open state ({ x, y, … } or null), closed by any
// outside click or Escape. The menu itself stops mousedown propagation so a
// click inside it lands on its item first.
function useCtxMenu() {
  const [ctxMenu, setCtxMenu] = useState(null);
  useEffect(() => {
    if (!ctxMenu) return undefined;
    const close = () => setCtxMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') setCtxMenu(null); };
    const t = setTimeout(() => {
      window.addEventListener('mousedown', close);
      window.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctxMenu]);
  return [ctxMenu, setCtxMenu];
}

function FolderRow({ folder, selected, onSelect, depth = 0, onEmptyFolder }) {
  // All folders load collapsed — user expands manually when they want to see
  // sub-folders. Previously top-level nodes started expanded, which gave a
  // busy sidebar on first load.
  const [expanded, setExpanded] = useState(false);
  // Right-click context menu — { x, y } when open, null when closed.
  // Only opens on emptyable folders so we don't show a confusing
  // "Empty Folder" option on Inbox / Sent / etc.
  const [ctxMenu, setCtxMenu] = useCtxMenu();
  const visibleChildren = (folder.childFolders || []).filter(c => !isHiddenFolder(c));
  const hasChildren = visibleChildren.length > 0;
  const isSelected = selected?.id === folder.id;
  const fkey = folderKey(folder);
  const canEmpty = EMPTYABLE.has(fkey) && !!onEmptyFolder;
  const mutedBadge = MUTED_BADGE.has(fkey);

  function onContextMenu(e) {
    if (!canEmpty) return;  // let the browser's default menu through on non-emptyable folders
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY });
  }

  return (
    <div>
      <div
        className={`${styles.folder} ${isSelected ? styles.folderActive : ''}`}
        style={{ paddingLeft: 10 + depth * 14 }}
        onClick={() => onSelect(folder)}
        onContextMenu={onContextMenu}
      >
        {hasChildren ? (
          <button
            className={styles.chevron}
            onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            <Icon name={expanded ? 'chevronDown' : 'chevronRight'} size={10} />
          </button>
        ) : (
          <span className={styles.chevronSpace} />
        )}

        <span className={styles.icon}>
          <Icon name={iconFor(folder.displayName)} size={14} />
        </span>
        <span className={styles.name}>{folder.displayName}</span>
        {folder.unreadItemCount > 0 && (
          <span className={`${styles.unread} ${mutedBadge ? styles.unreadMuted : ''}`}>
            {folder.unreadItemCount}
          </span>
        )}
      </div>

      {ctxMenu && canEmpty && (
        <div
          className={styles.folderCtxMenu}
          style={{ top: ctxMenu.y, left: ctxMenu.x }}
          onClick={e => e.stopPropagation()}
          onMouseDown={e => e.stopPropagation()}
          role="menu"
        >
          <button
            className={`${styles.folderCtxItem} ${styles.folderCtxDanger}`}
            onClick={() => { setCtxMenu(null); onEmptyFolder(folder); }}
            role="menuitem"
          >
            <Icon name="trashThin" size={13} /> Empty Folder
          </button>
        </div>
      )}

      {hasChildren && expanded && visibleChildren.map(child => (
        <FolderRow
          key={child.id}
          folder={child}
          selected={selected}
          onSelect={onSelect}
          depth={depth + 1}
          onEmptyFolder={onEmptyFolder}
        />
      ))}
    </div>
  );
}

function LabelsSection({ selectedLabel, onSelectLabel, labelCounts, onAddLabel, customLabels: customLabelsProp }) {
  const [expanded, toggleExpanded] = useSectionOpen('labels', false);
  const [fetchedLabels, setFetchedLabels] = useState([]);

  // Section 6 #10: prefer the parent-provided list — App already fetches
  // /api/labels/custom for the right-click Set-label menu (refreshed on the
  // same labelsRefreshKey), so we don't duplicate the call. Falls back to
  // fetching when no prop is passed (backward-compatible).
  const usingProp = Array.isArray(customLabelsProp);
  useEffect(() => {
    if (usingProp) return;
    let cancelled = false;
    fetch('/api/labels/custom')
      .then(r => r.json())
      .then(d => { if (!cancelled) setFetchedLabels(d.customLabels || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [usingProp]);
  const customLabels = usingProp ? customLabelsProp : fetchedLabels;

  // Built-in labels with their static palette + custom labels with
  // their server-assigned colour, merged and sorted alphabetically.
  const merged = [
    ...LABELS.map(name => ({ name, colour: LABEL_COLOURS[name], custom: false })),
    ...customLabels.map(c => ({ name: c.name, colour: c.colour, custom: true, id: c.id })),
  ].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader} onClick={toggleExpanded}>
        <span className={styles.sectionChevron}>
          <Icon name={expanded ? 'chevronDown' : 'chevronRight'} size={10} />
        </span>
        <span className={styles.sectionTitle}>Labels</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAddLabel?.(); }}
          title="Add a custom label"
          style={{
            marginLeft: 'auto',
            border: 0,
            background: 'transparent',
            color: 'var(--text-3)',
            cursor: 'pointer',
            width: 22,
            height: 22,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4,
          }}
        >
          <Icon name="plus" size={12} stroke={2.2} />
        </button>
      </div>
      {expanded && (
        <div>
          {merged.map(({ name, colour }) => (
            <div
              key={name}
              className={`${styles.customerItem} ${selectedLabel === name ? styles.customerItemActive : ''}`}
              onClick={() => onSelectLabel(name)}
            >
              <span
                className={styles.customerIcon}
                style={{ color: colour }}
                aria-hidden
              >
                <span style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: colour,
                }} />
              </span>
              <span className={styles.customerName}>{name}</span>
              {labelCounts?.[name] > 0 && (
                <span className={styles.sectionCount}>{labelCounts[name]}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Every flagged email in the Inbox, newest first; clicking one opens it in the
// reading pane, right-clicking offers Mark as done / Unflag (either takes it
// off the list).
function FlagsSection({ messages, more, loaded, selectedMsgId, onOpen, onUnflag, onDone }) {
  const [expanded, toggleExpanded] = useSectionOpen('flags', true);
  const [ctxMenu, setCtxMenu] = useCtxMenu();   // { x, y, msg }

  function onContextMenu(e, m) {
    if (!onUnflag && !onDone) return;  // nothing to offer — leave the browser's menu
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, msg: m });
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader} onClick={toggleExpanded}>
        <span className={styles.sectionChevron}>
          <Icon name={expanded ? 'chevronDown' : 'chevronRight'} size={10} />
        </span>
        <span className={styles.sectionTitle}>Flags</span>
        {messages.length > 0 && (
          <span className={styles.sectionCount}>{messages.length}{more ? '+' : ''}</span>
        )}
      </div>
      {expanded && (
        <div>
          {!loaded && messages.length === 0 && <div className={styles.loading}>Loading…</div>}
          {loaded && messages.length === 0 && <div className={styles.loading}>No flagged emails</div>}
          {messages.map(m => {
            const who = m.fromName || m.from || '(unknown sender)';
            const subject = m.subject || '(no subject)';
            return (
              <div
                key={m.id}
                className={`${styles.flagItem} ${selectedMsgId === m.id ? styles.flagItemActive : ''}`}
                onClick={() => onOpen?.(m)}
                onContextMenu={e => onContextMenu(e, m)}
                title={`${who}\n${subject}`}
              >
                <span className={styles.flagIcon} aria-hidden>
                  <Icon name="flag" size={12} stroke={2} />
                </span>
                <span className={styles.flagText}>
                  <span className={styles.flagWho}>{who}</span>
                  <span className={styles.flagSubject}>{subject}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {ctxMenu && (
        <div
          className={styles.folderCtxMenu}
          style={{ top: ctxMenu.y, left: ctxMenu.x }}
          onClick={e => e.stopPropagation()}
          onMouseDown={e => e.stopPropagation()}
          role="menu"
        >
          {onDone && (
            <button
              className={styles.folderCtxItem}
              onClick={() => { setCtxMenu(null); onDone(ctxMenu.msg); }}
              role="menuitem"
            >
              <span className={styles.flagCtxDone}><Icon name="check" size={13} stroke={2.2} /></span> Mark as done
            </button>
          )}
          {onUnflag && (
            <button
              className={styles.folderCtxItem}
              onClick={() => { setCtxMenu(null); onUnflag(ctxMenu.msg); }}
              role="menuitem"
            >
              <Icon name="flagOutline" size={13} /> Unflag
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CustomersSection({ customers, loading, selectedCustomer, onSelectCustomer }) {
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState('');

  const visible = filter.trim()
    ? customers.filter(c => c.name.toLowerCase().includes(filter.toLowerCase()))
    : customers;

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader} onClick={() => setExpanded(e => !e)}>
        <span className={styles.sectionChevron}>
          <Icon name={expanded ? 'chevronDown' : 'chevronRight'} size={10} />
        </span>
        <span className={styles.sectionTitle}>Customers</span>
        {customers.length > 0 && (
          <span className={styles.sectionCount}>{customers.length}</span>
        )}
      </div>

      {expanded && (
        <>
          <div className={styles.customerSearch}>
            <Icon name="search" size={11} />
            <input
              className={styles.customerSearchInput}
              placeholder="Search…"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            {filter && (
              <button
                className={styles.customerSearchClear}
                onClick={e => { e.stopPropagation(); setFilter(''); }}
              >✕</button>
            )}
          </div>
          <div>
            {loading && <div className={styles.loading}>Loading…</div>}
            {visible.map(c => (
              <div
                key={c.id}
                className={`${styles.customerItem} ${selectedCustomer?.id === c.id ? styles.customerItemActive : ''}`}
                onClick={() => onSelectCustomer(c)}
                title={c.domain}
              >
                <span className={styles.customerIcon}>
                  <Icon name="person" size={13} />
                </span>
                <span className={styles.customerName}>{c.name}</span>
              </div>
            ))}
            {!loading && visible.length === 0 && filter && (
              <div className={styles.loading}>No match</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function FolderTree({
  folders, archiveFolders = [], loading, selected, onSelect, onCompose,
  customers = [], loadingCustomers = false, selectedCustomer = null, onSelectCustomer,
  selectedLabel = null, onSelectLabel, labelCounts = {},
  // Custom-label add: parent owns the dialog state. labelsRefreshKey is
  // bumped after a successful create/delete; App re-fetches customLabels and
  // passes the fresh list down here (Section 6 #10 — no duplicate fetch).
  onAddLabel, labelsRefreshKey = 0, customLabels = null,
  // Flags section — App owns the list (lib/useFlaggedMail.js) so an in-app
  // flag / tick shows here at once; onOpenFlagged opens the email, and the
  // right-click menu's onDoneFlagged / onUnflagFlagged tick or clear its flag.
  flaggedMessages = [], flaggedMore = false, flaggedLoaded = false,
  selectedMsgId = null, onOpenFlagged, onUnflagFlagged, onDoneFlagged,
  // Callback invoked when the user picks "Empty Folder" from a
  // folder's right-click menu. Receives the folder object — App.js
  // wires it to handleEmptyFolder, which opens the same confirm
  // dialog the message-list-header button uses.
  onEmptyFolder,
  // Footer: shown at the bottom of the drawer below labels. On mobile this
  // is where logout lives (the top-bar pill is hidden) so the search bar
  // can have all the room to itself. On desktop the drawer is fixed-open
  // and this same footer renders as the persistent user/logout bar.
  // The footer button now opens a popup menu (Bot Rules / Show external
  // content / Sign out) so the logout pill in the top bar can stay
  // removed and Bot Rules has a permanent home.
  currentUser = null, onLogout,
  onOpenRules,
  showExternalContent = false,
  onToggleExternalContent,
  // Admin-only entry-point. When set + the current user is an OnPoint
  // admin (users.admin = 1), the account menu shows a "Open another
  // mailbox" item that triggers this callback.
  onOpenAnotherMailbox,
}) {
  const isAdmin = Number(currentUser?.admin) === 1;
  const sorted = sortFolders(folders);
  const composeButton = (
    <button className={styles.composeBtn} onClick={onCompose}>
      <Icon name="add" size={14} stroke={2.2} /> New Email
    </button>
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onDocClick(e) {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setMenuOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setMenuOpen(false); }
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <aside className={styles.sidebar}>
      {/* /V2 puts New Email at the foot, just above the account button; the
          classic app keeps it at the top. Moved in the markup, not with CSS
          order, so keyboard order follows what's on screen. */}
      {!V2 && composeButton}

      <div className={styles.list}>
        <MailboxSwitcher />
        {loading && <div className={styles.loading}>Loading…</div>}
        {sorted.map(f => (
          <FolderRow key={f.id} folder={f} selected={selected} onSelect={onSelect} onEmptyFolder={onEmptyFolder} />
        ))}
        {archiveFolders.map(f => (
          <FolderRow key={f.id} folder={f} selected={selected} onSelect={onSelect} onEmptyFolder={onEmptyFolder} />
        ))}
        <CustomersSection
          customers={customers}
          loading={loadingCustomers}
          selectedCustomer={selectedCustomer}
          onSelectCustomer={onSelectCustomer}
        />
        <LabelsSection
          key={labelsRefreshKey}
          selectedLabel={selectedLabel}
          onSelectLabel={onSelectLabel}
          labelCounts={labelCounts}
          onAddLabel={onAddLabel}
          customLabels={customLabels}
        />
        <FlagsSection
          messages={flaggedMessages}
          more={flaggedMore}
          loaded={flaggedLoaded}
          selectedMsgId={selectedMsgId}
          onOpen={onOpenFlagged}
          onUnflag={onUnflagFlagged}
          onDone={onDoneFlagged}
        />
      </div>

      {V2 && composeButton}

      {currentUser && (
        <div ref={menuRef} className={styles.accountWrap}>
          {menuOpen && (
            <div className={styles.accountMenu} role="menu">
              {onOpenRules && (
                <button
                  type="button"
                  role="menuitem"
                  className={styles.accountMenuItem}
                  onClick={() => { setMenuOpen(false); onOpenRules(); }}
                >
                  <Icon name="settings" size={13} />
                  <span>Bot Rules</span>
                </button>
              )}
              {isAdmin && onOpenAnotherMailbox && (
                <button
                  type="button"
                  role="menuitem"
                  className={styles.accountMenuItem}
                  onClick={() => { setMenuOpen(false); onOpenAnotherMailbox(); }}
                  title="Open another user's mailbox in a new Outlook tab"
                >
                  <Icon name="mail" size={13} />
                  <span>Open another mailbox</span>
                </button>
              )}
              {onToggleExternalContent && (
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={showExternalContent}
                  className={styles.accountMenuItem}
                  onClick={() => { onToggleExternalContent(); setMenuOpen(false); }}
                  title="Some emails contain images / links blocked by your organisation. Toggle this to render them anyway for the current pane."
                >
                  <span className={styles.accountToggle}>
                    {showExternalContent ? '☑' : '☐'}
                  </span>
                  <span>Show external content</span>
                </button>
              )}
              {/* Browser-zoom help row. Programmatic CSS zoom kept
                  introducing layout regressions (vh/vw + fixed-position
                  edge cases), so we surface the browser's own zoom
                  controls instead. Chrome and Firefox both remember
                  the chosen zoom per origin, so the user sets it once
                  per device. */}
              {/* /V2's way back to the current app (replaces the floating
                  "V2 preview" badge). Keeps the mailbox (?as=…). */}
              {V2 && (
                <a role="menuitem" className={styles.accountMenuItem} href={classicHref()}>
                  <Icon name="reply" size={13} />
                  <span>Back to classic MailPoint</span>
                </a>
              )}
              {(onOpenRules || onToggleExternalContent || V2) && onLogout && <div className={styles.accountMenuSep} />}
              {onLogout && (
                <button
                  type="button"
                  role="menuitem"
                  className={`${styles.accountMenuItem} ${styles.accountMenuDanger}`}
                  onClick={() => { setMenuOpen(false); onLogout(); }}
                >
                  <Icon name="logout" size={13} />
                  <span>Sign out</span>
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            className={styles.logoutRow}
            onClick={() => setMenuOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title="Account, settings and sign out"
          >
            <span className={styles.logoutAvatar} aria-hidden>
              {(currentUser.displayName || currentUser.user || currentUser.mailbox || '·')
                .split(/\s+/).slice(0, 2).map(w => (w[0] || '').toUpperCase()).join('') || '·'}
            </span>
            <span className={styles.logoutMeta}>
              <span className={styles.logoutName}>
                {currentUser.displayName || currentUser.user || 'Account'}
              </span>
              <span className={styles.logoutMailbox}>{currentUser.mailbox}</span>
            </span>
            <Icon name={menuOpen ? 'chevronDown' : 'chevronRight'} size={13} />
          </button>
        </div>
      )}
    </aside>
  );
}

